

import { useAgentOptions } from "../../hooks/useAgentOptions";

interface CreateDocumentModalProps {
  isOpen: boolean;
  newTitle: string;
  newCategory: string;
  newAgentType: string;
  newContent: string;
  creatingSubmitting: boolean;
  onClose: () => void;
  onTitleChange: (val: string) => void;
  onCategoryChange: (val: string) => void;
  onAgentTypeChange: (val: string) => void;
  onContentChange: (val: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export function CreateDocumentModal({
  isOpen,
  newTitle,
  newCategory,
  newAgentType,
  newContent,
  creatingSubmitting,
  onClose,
  onTitleChange,
  onCategoryChange,
  onAgentTypeChange,
  onContentChange,
  onSubmit,
}: CreateDocumentModalProps) {
  const agentOptions = useAgentOptions({ includeUniversal: true });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 dark:bg-black/70 backdrop-blur-xs">
      <form
        onSubmit={onSubmit}
        className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 shadow-xl max-w-xl w-full p-6 space-y-4"
      >
        <div>
          <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
            Create & Ingest Authoritative Document
          </h3>
          <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
            The content will be automatically segmented into chunks and embedded with FastEmbed.
          </p>
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">Document Title</label>
            <input
              type="text"
              required
              placeholder="e.g. Nosara Private Air Charter Logistics"
              value={newTitle}
              onChange={(e) => onTitleChange(e.target.value)}
              className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">Category</label>
              <input
                type="text"
                list="knowledge-categories"
                value={newCategory}
                onChange={(e) => onCategoryChange(e.target.value)}
                placeholder="Select or enter category..."
                className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
              />
              <datalist id="knowledge-categories">
                <option value="logistics">Logistics & Transfers</option>
                <option value="villas">Villas & Residences</option>
                <option value="policies">Policies & Cancellation</option>
                <option value="culinary">Culinary & Dining</option>
                <option value="activities">Activities & Surf</option>
                <option value="operations">General Operations</option>
                <option value="feedback">Feedback & Curation</option>
              </datalist>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">Scope</label>
              <select
                value={newAgentType}
                onChange={(e) => onAgentTypeChange(e.target.value)}
                className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
              >
                {agentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">Document Content</label>
            <textarea
              rows={6}
              required
              placeholder="Paste complete raw text here..."
              value={newContent}
              onChange={(e) => onContentChange(e.target.value)}
              className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2.5 text-xs font-mono text-stone-900 dark:text-stone-100 leading-relaxed placeholder:text-stone-400 dark:placeholder:text-stone-500 focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-xs font-medium text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={creatingSubmitting}
            className="px-4 py-2 rounded-lg bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white disabled:opacity-50"
          >
            {creatingSubmitting ? "Indexing..." : "Ingest & Index"}
          </button>
        </div>
      </form>
    </div>
  );
}
